import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { RecargasService, Operadora, TipoOperador, Producto } from 'src/app/services/recargas/recargas.service';

@Component({
  selector: 'app-recargas-admin',
  templateUrl: './recargas-admin.component.html',
  styleUrls: ['./recargas-admin.component.css']
})
export class RecargasAdminComponent implements OnInit {

  // Estados de carga
  loadingOperadoras: boolean = false;
  loadingTipos: boolean = false;
  loadingProductos: boolean = false;

  // Estados de toggle
  togglingOperador: { [key: string]: boolean } = {};
  togglingTipo: { [key: string]: boolean } = {};
  togglingProducto: { [key: string]: boolean } = {};

  // Datos
  operadoras: Operadora[] = [];
  tiposOperadores: TipoOperador[] = [];
  productos: Producto[] = [];

  // Filtros y selecciones
  selectedOperadora: string | null = null;
  selectedTipoOperador: string | null = null;
  searchOperadoras: string = '';
  searchTipos: string = '';
  searchProductos: string = '';

  // Pestañas activas
  activeTab: 'operadoras' | 'tipos' | 'productos' = 'operadoras';

  // Estado del modal de edición de secuencia
  isEditingSequence = false;
  editingProduct: Producto | null = null;
  editingSequence: any = null;
  sequenceError: string | null = null;
  isSavingSequence = false;

  constructor(
    private recargasService: RecargasService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadOperadoras();
  }

  /**
   * Carga todas las operadoras (activas e inactivas)
   */
  async loadOperadoras(): Promise<void> {
    try {
      this.loadingOperadoras = true;
      const response = await this.recargasService.getOperators({});
      
      if (response?.resp) {
        this.operadoras = response.resp.sort((a, b) => a.order - b.order);
      } else {
        this.operadoras = [];
      }
      
    } catch (error) {
      console.error('Error loading operadoras:', error);
      console.log('Error al cargar operadoras');
      this.operadoras = [];
    } finally {
      this.loadingOperadoras = false;
    }
  }

  /**
   * Carga tipos de operadores para una operadora específica
   */
  async loadTiposOperadores(operadoraCode: string): Promise<void> {
    try {
      this.loadingTipos = true;
      this.selectedOperadora = operadoraCode;
      
      const response = await this.recargasService.getTypeOperators(operadoraCode);
      
      if (response?.resp) {
        this.tiposOperadores = response.resp.sort((a, b) => a.order - b.order);
      } else {
        this.tiposOperadores = [];
      }
      
    } catch (error) {
      console.error('Error loading tipos operadores:', error);
      console.log('Error al cargar tipos de operadores');
      this.tiposOperadores = [];
    } finally {
      this.loadingTipos = false;
    }
  }

  /**
   * Carga productos para una operadora y tipo específicos
   */
  async loadProductos(operadoraCode: string, tipoCode: string): Promise<void> {
    try {
      this.loadingProductos = true;
      this.selectedTipoOperador = tipoCode;
      
      const response = await this.recargasService.getProducts(operadoraCode, tipoCode);
      
      if (response?.resp) {
        this.productos = response.resp.sort((a, b) => a.order - b.order);
      } else {
        this.productos = [];
      }
      
    } catch (error) {
      console.error('Error loading productos:', error);
      console.log('Error al cargar productos');
      this.productos = [];
    } finally {
      this.loadingProductos = false;
    }
  }

  /**
   * Cambia el estado de una operadora
   */
  async toggleOperadorStatus(operadora: Operadora): Promise<void> {
    const key = operadora.code;
    
    try {
      this.togglingOperador[key] = true;
      const newStatus = !operadora.status;
      
      const response = await this.recargasService.toggleOperatorStatus(operadora.code, newStatus);
      
      if (response?.operator) {
        // Actualiza el estado local
        const index = this.operadoras.findIndex(op => op.code === operadora.code);
        if (index !== -1) {
          this.operadoras[index].status = newStatus;
          // Forzar detección de cambios
          this.cdr.detectChanges();
        }
        
        const statusText = newStatus ? 'activada' : 'inactivada';
        console.log(`Operadora ${statusText} exitosamente`);
      }
      
    } catch (error) {
      console.error('Error toggling operadora status:', error);
      console.log('Error al cambiar estado de la operadora');
    } finally {
      delete this.togglingOperador[key];
    }
  }

  /**
   * Cambia el estado de un tipo de operador
   */
  async toggleTipoOperadorStatus(tipo: TipoOperador): Promise<void> {
    const key = tipo.code;
    
    try {
      this.togglingTipo[key] = true;
      const newStatus = !tipo.status;
      
      const response = await this.recargasService.toggleTypeOperatorStatus(tipo.code, newStatus);
      
      if (response?.typeOperator) {
        // Actualiza el estado local
        const index = this.tiposOperadores.findIndex(t => t.code === tipo.code);
        if (index !== -1) {
          this.tiposOperadores[index].status = newStatus;
          // Forzar detección de cambios
          this.cdr.detectChanges();
        }
        
        const statusText = newStatus ? 'activado' : 'inactivado';
        console.log(`Tipo de operador ${statusText} exitosamente`);
      }
      
    } catch (error) {
      console.error('Error toggling tipo operador status:', error);
      console.log('Error al cambiar estado del tipo de operador');
    } finally {
      delete this.togglingTipo[key];
    }
  }

  /**
   * Cambia el estado de un producto
   */
  async toggleProductoStatus(producto: Producto): Promise<void> {
    const key = producto.code;
    
    try {
      this.togglingProducto[key] = true;
      const newStatus = !producto.status;
      
      const response = await this.recargasService.toggleProductOperatorStatus(producto.code, newStatus);
      
      if (response?.productOperator) {
        // Actualiza el estado local
        const index = this.productos.findIndex(p => p.code === producto.code);
        if (index !== -1) {
          this.productos[index].status = newStatus;
          
          // Forzar detección de cambios
          this.cdr.detectChanges();
        }
        
        const statusText = newStatus ? 'activado' : 'inactivado';
        console.log(`Producto ${statusText} exitosamente`);
      }
      
    } catch (error) {
      console.error('Error toggling producto status:', error);
      console.log('Error al cambiar estado del producto');
    } finally {
      delete this.togglingProducto[key];
    }
  }

  /**
   * Filtra operadoras por búsqueda (muestra todas: activas e inactivas)
   */
  get filteredOperadoras(): Operadora[] {
    if (!this.searchOperadoras.trim()) {
      return this.operadoras;
    }
    
    const search = this.searchOperadoras.toLowerCase();
    return this.operadoras.filter(op => 
      op.name.toLowerCase().includes(search) ||
      op.code.toLowerCase().includes(search) ||
      op.description?.toLowerCase().includes(search)
    );
  }

  /**
   * Filtra tipos de operadores por búsqueda (muestra todos: activos e inactivos)
   */
  get filteredTiposOperadores(): TipoOperador[] {
    if (!this.searchTipos.trim()) {
      return this.tiposOperadores;
    }
    
    const search = this.searchTipos.toLowerCase();
    return this.tiposOperadores.filter(tipo =>
      tipo.name.toLowerCase().includes(search) ||
      tipo.code.toLowerCase().includes(search) ||
      tipo.description?.toLowerCase().includes(search)
    );
  }

  /**
   * Filtra productos por búsqueda (muestra todos: activos e inactivos)
   */
  get filteredProductos(): Producto[] {
    if (!this.searchProductos.trim()) {
      return this.productos;
    }
    
    const search = this.searchProductos.toLowerCase();
    return this.productos.filter(producto =>
      producto.name.toLowerCase().includes(search) ||
      producto.code.toLowerCase().includes(search) ||
      producto.description?.toLowerCase().includes(search)
    );
  }

  /**
   * Cambia la pestaña activa
   */
  setActiveTab(tab: 'operadoras' | 'tipos' | 'productos'): void {
    this.activeTab = tab;
  }

  /**
   * Resetea selecciones al cambiar de operadora
   */
  onOperadoraChange(): void {
    this.selectedTipoOperador = null;
    this.productos = [];
    this.tiposOperadores = [];
  }

  /**
   * Resetea productos al cambiar de tipo
   */
  onTipoOperadorChange(): void {
    this.productos = [];
  }

  /**
   * Navega a la vista normal de operadoras
   */
  navigateToOperadoras(): void {
    this.router.navigate(['/console/recargas/operadoras']);
  }

  /**
   * Abre el modal para editar la secuencia de un producto
   */
  onEditSequence(producto: Producto): void {
    this.editingProduct = producto;
    this.isEditingSequence = true;
    this.sequenceError = null;
    
    // Clonar la secuencia actual para edición
    if (producto.params?.sequence?.BS) {
      this.editingSequence = {
        BS: {
          default: producto.params.sequence.BS.default,
          list: [...producto.params.sequence.BS.list]
        }
      };
    } else {
      // Secuencia por defecto si no existe
      this.editingSequence = {
        BS: {
          default: producto.min || 50,
          list: [50, 100, 200, 500]
        }
      };
    }
  }

  /**
   * Cierra el modal de edición de secuencia
   */
  onCloseSequenceModal(): void {
    this.isEditingSequence = false;
    this.editingProduct = null;
    this.editingSequence = null;
    this.sequenceError = null;
    this.isSavingSequence = false;
  }

  /**
   * Agrega un nuevo monto a la lista
   */
  onAddNewAmount(value: string): void {
    if (!this.editingSequence?.BS?.list || !value) return;
    
    const newAmount = parseInt(value);
    if (isNaN(newAmount) || newAmount <= 0) {
      this.sequenceError = 'Ingrese un monto válido mayor a 0';
      return;
    }

    if (this.editingSequence.BS.list.includes(newAmount)) {
      this.sequenceError = 'Este monto ya existe en la lista';
      return;
    }

    // Limpiar error previo
    this.sequenceError = null;
    
    // Agregar y ordenar
    this.editingSequence.BS.list.push(newAmount);
    this.editingSequence.BS.list.sort((a: number, b: number) => a - b);

    // Establecer el primer monto como por defecto si no hay uno establecido
    if (!this.editingSequence.BS.default || this.editingSequence.BS.list.length === 1) {
      this.editingSequence.BS.default = this.editingSequence.BS.list[0];
    }
  }

  /**
   * Elimina un monto de la lista
   */
  onRemoveAmount(amount: number): void {
    if (!this.editingSequence?.BS?.list) return;
    
    const index = this.editingSequence.BS.list.indexOf(amount);
    if (index > -1) {
      this.editingSequence.BS.list.splice(index, 1);
      
      // Si eliminamos el monto por defecto, establecer el primero como nuevo por defecto
      if (amount === this.editingSequence.BS.default && this.editingSequence.BS.list.length > 0) {
        this.editingSequence.BS.default = this.editingSequence.BS.list[0];
      }
    }
  }

  /**
   * Establece el monto por defecto
   */
  onSetDefault(amount: number): void {
    if (this.editingSequence?.BS) {
      this.editingSequence.BS.default = amount;
    }
  }

  /**
   * Guarda los cambios de la secuencia
   */
  async onSaveSequence(): Promise<void> {
    if (!this.editingProduct || !this.editingSequence) return;

    this.isSavingSequence = true;
    this.sequenceError = null;

    try {
      // Validar que el monto por defecto esté en la lista
      if (!this.editingSequence.BS.list.includes(this.editingSequence.BS.default)) {
        this.sequenceError = 'El monto por defecto debe estar incluido en la lista de montos disponibles';
        this.isSavingSequence = false;
        return;
      }

      // Validar que haya al menos un monto
      if (this.editingSequence.BS.list.length === 0) {
        this.sequenceError = 'Debe incluir al menos un monto en la lista';
        this.isSavingSequence = false;
        return;
      }

      // Actualizar secuencia en el servidor
      const response = await this.recargasService.updateProductSequence(
        this.editingSequence,
        this.editingProduct.code
      );

      if (response?.res) {
        // Actualizar el producto local
        this.editingProduct.params = {
          ...this.editingProduct.params,
          sequence: { ...this.editingSequence }
        };

        // Forzar detección de cambios
        this.cdr.detectChanges();

        // Cerrar modal
        this.onCloseSequenceModal();
        
        // Mostrar mensaje de éxito
        console.log('Secuencia actualizada exitosamente');
      } else {
        this.sequenceError = 'Error al actualizar la secuencia. Intente nuevamente.';
      }
    } catch (error: any) {
      console.error('Error guardando secuencia:', error);
      this.sequenceError = error?.error?.msg || 'Error al actualizar la secuencia. Intente nuevamente.';
    } finally {
      this.isSavingSequence = false;
    }
  }

  /**
   * TrackBy function para optimizar el renderizado de montos
   */
  trackByMonto(index: number, monto: number): number {
    return monto;
  }
}
